<template>
  <v-dialog
    ref="dialog"
    v-model="isVisible"
    max-width="440"
    persistent
    content-class="ds-confirm-dialog-content"
    @keydown.esc="onCancel"
  >
    <v-card class="ds-confirm" v-if="opts">
      <!-- Header com gradiente por tipo -->
      <v-card-title
        class="ds-confirm-header"
        :class="`ds-confirm-header--${opts.variant}`"
      >
        <v-icon color="white" class="mr-2">{{ opts.headerIcon }}</v-icon>
        <span class="ds-confirm-title">{{ opts.title }}</span>
        <v-spacer></v-spacer>
        <v-btn icon dark class="close-btn" @click="onCancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Corpo: ícone + mensagem, num cartão com o filete na cor do alerta -->
      <v-card-text class="ds-dialog-body">
        <section class="ds-form-section" :class="sectionVariantClass">
          <div class="text-center pt-2">
            <v-icon size="46" :color="bodyIconColor" class="mb-3">
              {{ opts.bodyIcon }}
            </v-icon>
            <p class="ds-confirm-message" v-html="safeMessage"></p>
            <p v-if="opts.hint" class="ds-confirm-hint">{{ opts.hint }}</p>
          </div>

          <!--
            salvia-776 — ata de 06/08: "toda vez que tentarem excluir alguma
            coisa tem que colocar o motivo".

            Fica AQUI, no diálogo único, e não em cada tela: são 34 pontos de
            exclusão no sistema, e todos já passam por este componente. Um
            campo aqui é 34 telas cobertas — e a 35ª, que alguém escrever
            amanhã, já nasce coberta.

            Lista fechada em vez de texto livre puro: dá relatório e evita que
            o campo vire despejo de "x". O texto livre existe porque nenhuma
            lista cobre tudo, e só aparece ao escolher "Outro".
          -->
          <div v-if="exigeMotivo" class="mt-4">
            <v-select
              v-model="motivoEscolhido"
              :items="motivos"
              label="Motivo da exclusão"
              prepend-inner-icon="mdi-comment-alert-outline"
              outlined
              dense
              hide-details="auto"
              :menu-props="{ offsetY: true }"
            />

            <v-textarea
              v-if="motivoEscolhido === motivoOutro"
              v-model="motivoTexto"
              label="Descreva o motivo"
              :counter="motivoMinimo"
              :rules="[regraDoTextoLivre]"
              outlined
              dense
              rows="2"
              auto-grow
              class="mt-3"
            />
          </div>
        </section>
      </v-card-text>

      <!-- Ações -->
      <v-card-actions class="ds-confirm-actions ds-dialog-actions">
        <v-spacer></v-spacer>
        <v-btn
          v-if="!opts.alertOnly"
          text
          class="ds-confirm-cancel mr-2"
          @click="onCancel"
        >
          {{ opts.cancelText || "Cancelar" }}
        </v-btn>
        <v-btn
          :color="opts.confirmColor"
          depressed
          class="ds-confirm-ok px-6"
          :disabled="!motivoValido"
          @click="onAccept"
        >
          {{ opts.confirmText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  confirmState,
  acceptConfirm,
  acceptDelete,
  cancelConfirm,
  MOTIVOS_DE_EXCLUSAO,
  MOTIVO_OUTRO,
  MOTIVO_MINIMO,
} from "@/services/confirmService";
import { escapeHtml, sanitizeRichHtml } from "@/utils/sanitizeHtml";

// Só formatação simples faz sentido num diálogo de confirmação.
const CONFIRM_TAGS = new Set(["B", "STRONG", "I", "EM", "BR"]);

/**
 * Renderer único (singleton) do serviço de confirmação.
 * Montado uma vez em App.vue. Não recebe props — lê o estado global e é
 * acionado via `this.$confirm({...})`.
 */
export default {
  name: "GlobalConfirmDialog",

  data() {
    return {
      motivoEscolhido: null,
      motivoTexto: "",
    };
  },

  computed: {
    // salvia-776 — expostos ao template.
    motivos: () => MOTIVOS_DE_EXCLUSAO,
    motivoOutro: () => MOTIVO_OUTRO,
    motivoMinimo: () => MOTIVO_MINIMO,

    /** Este diálogo específico pede motivo? Vem do preset; ver confirmService. */
    exigeMotivo() {
      return this.opts.exigeMotivo === true;
    },

    /**
     * O texto que vai para a auditoria: a opção escolhida, ou o texto livre
     * quando ela for "Outro".
     */
    motivoFinal() {
      if (!this.exigeMotivo) return null;

      return this.motivoEscolhido === MOTIVO_OUTRO
        ? this.motivoTexto.trim()
        : this.motivoEscolhido;
    },

    /**
     * Trava o botão "Excluir" até o motivo estar válido.
     *
     * Escolher uma opção da lista JÁ é o motivo e não pede texto -- o mínimo
     * de caracteres só existe para o "Outro", que é onde caberia um "x".
     */
    motivoValido() {
      if (!this.exigeMotivo) return true;
      if (!this.motivoEscolhido) return false;

      return this.motivoEscolhido === MOTIVO_OUTRO
        ? this.motivoTexto.trim().length >= MOTIVO_MINIMO
        : true;
    },
    state() {
      return confirmState;
    },
    opts() {
      return confirmState.opts;
    },
    isVisible: {
      get() {
        return confirmState.visible;
      },
      set(val) {
        // Fechar pelo overlay/esc conta como cancelar.
        if (!val) cancelConfirm();
      },
    },
    bodyIconColor() {
      return (
        {
          danger: "error",
          warning: "warning",
          success: "success",
          primary: "primary",
        }[this.opts.variant] || "primary"
      );
    },
    /** Filete do cartão na mesma cor do cabeçalho (verde fica no padrão). */
    sectionVariantClass() {
      const v = this.opts && this.opts.variant;
      return ["danger", "warning", "success"].includes(v)
        ? `ds-form-section--${v}`
        : "";
    },
    /**
     * Mensagem renderizada com v-html — por isso é sanitizada AQUI, no único
     * ponto de renderização, protegendo todos os ~50 chamadores de $confirm de
     * uma vez (vários interpolam dados digitados pelo usuário, como nome de
     * medicamento ou de exame).
     *
     * Estratégia: escapa TUDO e depois restaura apenas um conjunto pequeno de
     * tags de formatação, sem atributos. Assim `<img src=x onerror=...>` fica
     * inerte, mas `<strong>` continua funcionando.
     *
     * Por que aqui NÃO usamos o DOMPurify sozinho: ele APAGA o que não é
     * permitido, e este diálogo pergunta "tem certeza que quer excluir X?".
     * Uma clínica chamada `Clínica <A>` apareceria com o nome vazio, e o
     * usuário confirmaria uma exclusão sem saber do quê. Escapando, o nome
     * aparece literal e continua inofensivo. O DOMPurify entra depois, como
     * rede de segurança: se um dia alguém afrouxar a regex acima, ele segura.
     */
    safeMessage() {
      const escaped = escapeHtml(this.opts.message);

      // Só tags simples e sem atributos são reabilitadas.
      const withTags = escaped.replace(
        /&lt;(\/?)(b|strong|i|em|br)\s*\/?&gt;/gi,
        (_match, closing, tag) => `<${closing}${tag.toLowerCase()}>`,
      );

      // Quebras de linha continuam funcionando como antes.
      const withBreaks = withTags.replace(/\n/g, "<br>");

      return sanitizeRichHtml(withBreaks, { allowedTags: CONFIRM_TAGS });
    },
  },

  watch: {
    isVisible(val) {
      // salvia-776: zera o motivo a cada abertura. Sem isto, o texto digitado
      // numa exclusão ficaria pré-preenchido na próxima -- e a auditoria
      // registraria a justificativa errada, com ar de justificativa certa.
      if (val) {
        this.motivoEscolhido = null;
        this.motivoTexto = "";
      }

      // Ao abrir, garante que o confirm fique acima de overlays custom (ex.: o
      // .custom-drawer do chat, z-index 999). O Vuetify calcula o z-index do
      // dialog (~202) ignorando overlays não-gerenciados por ele, então o
      // confirm nascia ATRÁS da janela de chat. Subimos o wrapper posicionado
      // manualmente — robusto e independente de suporte a :has() no CSS.
      if (val) {
        this.$nextTick(this.raiseAboveOverlays);
        // Reaplica após a transição de entrada, caso o Vuetify reescreva o style.
        requestAnimationFrame(() => requestAnimationFrame(this.raiseAboveOverlays));
      }
    },
  },

  /**
   * salvia-776: o Vuetify reaplica `zIndex: activeZIndex` como estilo inline a
   * CADA render (VDialog.js:245). Sem isto, o primeiro re-render -- escolher o
   * motivo da exclusao, por exemplo -- desfazia o `raiseAboveOverlays()` da
   * abertura e o dialogo caia para tras do menu lateral, ainda funcionando mas
   * invisivel.
   *
   * Quem resolve de verdade e a regra `!important` em salvia-dialogs.css, que
   * estilo inline nao reescreve. Isto aqui e a rede para navegador sem
   * suporte a `:has()`.
   */
  updated() {
    if (confirmState.visible) this.raiseAboveOverlays();
  },

  methods: {
    onAccept() {
      if (!this.motivoValido) return;

      if (this.exigeMotivo) {
        acceptDelete(this.motivoFinal);

        return;
      }

      acceptConfirm();
    },

    /** Regra do campo livre — a mensagem diz o que falta, não só que está errado. */
    regraDoTextoLivre(v) {
      const texto = (v || "").trim();

      return (
        texto.length >= MOTIVO_MINIMO ||
        `Descreva com pelo menos ${MOTIVO_MINIMO} caracteres.`
      );
    },
    onCancel() {
      cancelConfirm();
    },
    raiseAboveOverlays() {
      // O content-class cai no .v-dialog interno; o z-index de empilhamento vive
      // no pai posicionado .v-dialog__content. Subimos esse pai.
      const inner = document.querySelector(".v-dialog.ds-confirm-dialog-content");
      const wrapper = inner && inner.closest(".v-dialog__content");
      if (wrapper) wrapper.style.zIndex = "10010";
    },
  },
};
</script>

<style scoped>
.ds-confirm {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

/* Header */
.ds-confirm-header {
  display: flex;
  align-items: center;
  color: var(--ds-white) !important;
  padding: 14px 22px !important;
  position: relative;
}
.ds-confirm-title {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: var(--ds-white);
  position: relative;
  z-index: 1;
}
.ds-confirm-header > * {
  position: relative;
  z-index: 1;
}
.ds-confirm-header::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 30px 30px;
  mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
  pointer-events: none;
}

/* Variantes de gradiente */
.ds-confirm-header--primary {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-dark) 60%, var(--ds-primary-darker) 100%) !important;
}
.ds-confirm-header--danger {
  background: linear-gradient(135deg, var(--ds-danger) 0%, var(--ds-danger-mid) 60%, var(--ds-danger-light) 100%) !important;
}
.ds-confirm-header--warning {
  background: linear-gradient(135deg, var(--ds-warning) 0%, var(--ds-warning-mid) 60%, var(--ds-warning-light) 100%) !important;
}
.ds-confirm-header--success {
  background: linear-gradient(135deg, var(--ds-success) 0%, var(--ds-success-mid) 60%, var(--ds-success-light) 100%) !important;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* Corpo */
.ds-confirm-message {
  font-size: 14px;
  line-height: 1.55;
  color: var(--ds-text);
  margin: 0;
}
.ds-confirm-hint {
  font-size: 12px;
  color: var(--ds-text-muted);
  margin: 8px 0 0;
}

/* Ações */
.ds-confirm-actions {
  background: var(--ds-blue-50);
  border-top: 1px solid var(--ds-blue-100);
}
.ds-confirm-cancel,
.ds-confirm-ok {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  border-radius: 8px !important;
}
</style>
